import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import CustomTooltip from "../../CustomTooltip";
import { DeleteIcon, LoadingIcon } from "../../../assets/icons/Icons";
import { Link } from "react-router-dom";
import MessageError from "../../MessageError";
import { ValidateCard } from "../../../api/sell-orders/validateCard";
import { getCardByPrn } from "../../../api/sell-orders/getCardByPrn";

const DEBOUNCE_DELAY = 500;

const CardRow = ({ rowIndex, rowData, onFieldChange, removeRow, ...props }) => {
    const [buttonDisabled, setButtonDisabled] = useState(true);
    const [validationLoading, setValidationLoading] = useState(false);
    const [error, setError] = useState('');

    const [localProcessorReference, setLocalProcessorReference] = useState(rowData.processor_reference || '');
    const [localLoadAmount, setLocalLoadAmount] = useState(rowData.load_amount || '');

    const { t } = useTranslation();

    const handleValidate = async () => {
        setError('');
        setValidationLoading(true);
        try {
            const [response, cardRes] = await Promise.all([
                ValidateCard({ office_id: props.formData.office_id, processor_reference: rowData.processor_reference }),
                getCardByPrn({ prn: rowData.processor_reference }),
            ]);
            props.setFormData && props.setFormData(prevData => ({
                ...prevData,
                // Optional all the way down: a cardholder with an empty accounts
                // array reaches [0] and would throw on a bare .program_id.
                program_id: cardRes?.data?.accounts?.[0]?.program_id ?? null
            }))
            if (response?.error?.message) {
                setError(response.error.message);
            } else {
                setError('');
                onFieldChange("card_type", "Oxford Gift Card PLUS"); // update card_type
            }
        } catch (err) {
            // A rejected lookup used to be logged and nothing else, leaving the
            // row looking untouched.
            setError(err?.message || t("Could not validate this card, please try again."));
        } finally {
            // In finally so the button leaves its loading state on every path,
            // not just the ones that return a response.
            setValidationLoading(false);
        }
    };

    // Debounce processor_reference
    useEffect(() => {
        const handler = setTimeout(() => {
            if (localProcessorReference !== rowData.processor_reference) {
                onFieldChange("processor_reference", localProcessorReference);
            }
        }, DEBOUNCE_DELAY);

        return () => clearTimeout(handler);
    }, [localProcessorReference]);

    // Debounce load_amount
    useEffect(() => {
        const handler = setTimeout(() => {
            if (localLoadAmount !== rowData.load_amount) {
                onFieldChange("load_amount", localLoadAmount);
            }
        }, DEBOUNCE_DELAY);

        return () => clearTimeout(handler);
    }, [localLoadAmount]);

    useEffect(() => {
        if ((rowData.processor_reference?.length === 12 || rowData.processor_reference?.length === 10) && props.formData.office_id) {
            setButtonDisabled(false);
        } else {
            setButtonDisabled(true);
        }
    }, [rowData.processor_reference, props.formData.office_id]);

    return (
        <>
            <td>
                <input
                    type="number"
                    placeholder={t("Proxy Number")}
                    value={localProcessorReference}
                    onChange={(e) => {
                        if (e.target.value >= 0) {
                            setLocalProcessorReference(e.target.value);
                        }
                    }}
                    onWheel={(e) => e.target.blur()}
                    onKeyDown={(e) => { if (e.key === 'ArrowUp' || e.key === 'ArrowDown') e.preventDefault(); }}
                />
                {error && <MessageError error={error} />}
            </td>
            <td>
                <button
                    className={`btn btn-primary btn-small ${validationLoading ? 'btn-loading' : ''}`}
                    onClick={handleValidate}
                    disabled={buttonDisabled}
                >
                    {validationLoading ? <LoadingIcon /> : ''}
                    {t("Validate")}
                </button>
            </td>
            <td>
                <input
                    type="text"
                    placeholder={t("Card Type")}
                    value={rowData.card_type || ""}
                    onChange={(e) => onFieldChange("card_type", e.target.value)}
                    disabled
                />
            </td>
            <td>
                <input
                    type="number"
                    placeholder={t("Amount")}
                    value={localLoadAmount}
                    onChange={(e) => {
                        if (e.target.value >= 0) {
                            setLocalLoadAmount(e.target.value);
                        }
                    }}
                    onWheel={(e) => e.target.blur()}
                    onKeyDown={(e) => { if (e.key === 'ArrowUp' || e.key === 'ArrowDown') e.preventDefault(); }}
                />
            </td>
            <td className="text-center">
                <Link to="#" onClick={(e) => { e.preventDefault(); removeRow(rowIndex); }}>
                    <CustomTooltip icon={<DeleteIcon />} message={t("Remove Row")} />
                </Link>
            </td>
        </>
    );
};

export default CardRow;
